/**
 * PairPolicyModal - Grouped Replacement Policy Modal
 * 
 * Handles different prompt types based on policy strength:
 * - INFO_BANNER: Yellow info banner (shown inline, not modal)
 * - RECOMMEND_MODAL: Soft recommendation modal
 * - OVERRIDE_MODAL: Blocking modal requiring reason selection
 * - AUTO_DRAFT: Both sides auto-added with confirmation
 * 
 * Separate from mandatory bundles (brakes) which don't show modals.
 */

import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertTriangle, Shield, CheckCircle2, Plus, X, AlertCircle } from 'lucide-react';

const OVERRIDE_REASON_LABELS = {
  customer_request: "Customer specifically requested single side",
  budget_constraint: "Budget constraint discussed with customer",
  opposite_recently_done: "Opposite side replaced within last 12 months",
  opposite_inspected_ok: "Opposite side inspected and confirmed serviceable",
  will_monitor: "Will monitor opposite side at next service",
  other: "Other reason"
};

export function PairPolicyModal({
  isOpen,
  onClose,
  promptType,
  promptData,
  onAddBothSides,
  onAddSingleSide,
  onSubmitOverride
}) {
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReasonText, setOtherReasonText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !promptData) return null;

  const {
    component_family,
    display_name,
    pair_scope,
    policy_strength,
    opposite_position,
    missing_positions,
    foh_prompt,
    override_reasons
  } = promptData;

  const handleAddBoth = () => {
    onAddBothSides?.();
    onClose();
  };

  const handleAddSingle = () => {
    onAddSingleSide?.();
    onClose();
  };

  const handleSubmitOverride = async () => {
    if (!selectedReason) return;
    
    if (selectedReason === 'other' && !otherReasonText.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmitOverride?.(selectedReason, selectedReason === 'other' ? otherReasonText : null);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // RECOMMEND_MODAL - Soft recommendation
  if (promptType === 'RECOMMEND_MODAL') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pair-policy-modal">
        <Card className="w-full max-w-md">
          <CardHeader className="pb-3 border-b border-amber-200 bg-amber-50">
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Pair Recommendation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-semibold text-amber-800 mb-2">
                {display_name}
              </p>
              <p className="text-sm text-amber-700">
                {foh_prompt || `This item is typically replaced as a pair for balanced performance.`}
              </p>
            </div>
            
            <p className="text-sm text-slate-600">
              Would you like to add both sides to ensure balanced performance?
            </p>
            
            <div className="flex flex-col gap-2 pt-2">
              <Button 
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleAddBoth}
                data-testid="pair-add-both-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Both Sides
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleAddSingle}
                data-testid="pair-add-single-btn"
              >
                Add Single Side Only
              </Button>
              <Button 
                variant="ghost" 
                className="w-full text-slate-500"
                onClick={onClose}
                data-testid="pair-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // OVERRIDE_MODAL - Blocking modal requiring reason
  if (promptType === 'OVERRIDE_MODAL') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pair-override-modal">
        <Card className="w-full max-w-md">
          <CardHeader className="pb-3 border-b border-red-200 bg-red-50">
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Shield className="w-5 h-5" />
              Single-Side Quote Override Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-semibold text-red-800 mb-2">
                {display_name}
              </p>
              <p className="text-sm text-red-700">
                {foh_prompt || `This component should normally be replaced in pairs for vehicle safety.`}
              </p>
            </div>
            
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-700 mb-3">
                To quote only one side, please select a reason:
              </p>
              <div className="space-y-2">
                {override_reasons && Object.entries(override_reasons).map(([code, label]) => (
                  <label 
                    key={code} 
                    className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                      selectedReason === code ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-100'
                    }`}
                  >
                    <input
                      type="radio"
                      name="override_reason"
                      value={code}
                      checked={selectedReason === code}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      className="mt-1"
                    />
                    <span className="text-sm text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
              
              {selectedReason === 'other' && (
                <textarea
                  placeholder="Please specify the reason..."
                  value={otherReasonText}
                  onChange={(e) => setOtherReasonText(e.target.value)}
                  className="mt-3 w-full p-2 border border-slate-300 rounded text-sm"
                  rows={2}
                />
              )}
            </div>
            
            <div className="flex flex-col gap-2 pt-2">
              <Button 
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                onClick={handleAddBoth}
                data-testid="pair-override-add-both-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Both Sides (Recommended)
              </Button>
              <Button 
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleSubmitOverride}
                disabled={!selectedReason || (selectedReason === 'other' && !otherReasonText.trim()) || isSubmitting}
                data-testid="pair-override-confirm-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Confirming...' : 'Confirm Single-Side Quote'}
              </Button>
              <Button 
                variant="ghost" 
                className="w-full text-slate-500"
                onClick={onClose}
                data-testid="pair-override-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // AUTO_DRAFT - Both sides auto-added, confirm or remove
  if (promptType === 'AUTO_DRAFT') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pair-auto-draft-modal">
        <Card className="w-full max-w-md">
          <CardHeader className="pb-3 border-b border-green-200 bg-green-50">
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" />
              Both Sides Added
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="font-semibold text-green-800 mb-2">
                {display_name}
              </p>
              <p className="text-sm text-green-700">
                Both sides have been added to your quote. This is the recommended approach for balanced performance.
              </p>
            </div>
            
            <p className="text-sm text-slate-600">
              You can remove the opposite side if not required.
            </p>
            
            <div className="flex flex-col gap-2 pt-2">
              <Button 
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                onClick={handleAddBoth}
                data-testid="pair-auto-keep-both-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Keep Both Sides
              </Button>
              <Button 
                variant="outline" 
                className="w-full text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={handleAddSingle}
                data-testid="pair-auto-remove-opposite-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Remove Opposite Side
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fallback - shouldn't happen, but just in case
  return null;
}

/**
 * PairInfoBanner - Inline info banner for INFO_ONLY policy strength
 * 
 * This is displayed inline (not as a modal) when adding INFO_ONLY items.
 */
export function PairInfoBanner({ display_name, foh_prompt, onDismiss }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3" data-testid="pair-info-banner">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-blue-700">
            {foh_prompt || `${display_name} is sometimes replaced as a pair. Consider if the opposite side also needs attention.`}
          </p>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="text-blue-400 hover:text-blue-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * MandatoryBundleBanner - Info banner for mandatory bundles (brakes)
 * 
 * This is displayed when adding brake items to explain the axle-set requirement.
 */
export function MandatoryBundleBanner({ display_name, message }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3" data-testid="mandatory-bundle-banner">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700">{display_name}</p>
          <p className="text-xs text-slate-500 mt-1">
            {message || `Will be quoted as an axle set. This is standard practice.`}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * PairStatusBadge - Badge for job group cards showing pair status
 */
export function PairStatusBadge({ pairComplete, hasOverride, overrideReason }) {
  if (pairComplete) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
        <CheckCircle2 className="w-3 h-3" />
        PAIR ✓
      </span>
    );
  }
  
  if (hasOverride) {
    return (
      <span 
        className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full cursor-help"
        title={`Override: ${OVERRIDE_REASON_LABELS[overrideReason] || overrideReason}`}
      >
        1/2
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
      <AlertTriangle className="w-3 h-3" />
      PAIR?
    </span>
  );
}

export default PairPolicyModal;
